import React, { useEffect, useMemo, useState } from 'react';
import { FaCloudUploadAlt, FaDownload, FaTrash } from 'react-icons/fa';
import * as clientService from '../../services/client.service';
import * as documentService from '../../services/document.service';
import PageHeader from '../../components/PageHeader/PageHeader';
import './DMS.css';

const formatDateTime = (value) => {
    if (!value) return '-';
  
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
  
    return d.toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };
  

const DMS = () => {
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [documentName, setDocumentName] = useState('');
  const [fileItem, setFileItem] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchClients = async () => {
      const result = await clientService.getClientList();
      setClients(Array.isArray(result.data) ? result.data : []);
    };
    fetchClients();
  }, []);

  useEffect(() => {
    if (!selectedClientId) {
      setDocuments([]);
      setLastUpdated(null);
      return;
    }
    const fetchDocuments = async () => {
      try {
        setIsLoading(true);
        const docs = await documentService.getClientDocuments(selectedClientId);
        setDocuments(docs);
        if (docs.length > 0) {
          setLastUpdated(new Date(docs[0].uploadedAt).toISOString());
        } else {
          setLastUpdated(null);
        }
      } catch (error) {
        console.error('Error fetching documents:', error);
        setMessage({ type: 'danger', text: 'Failed to fetch documents.' });
      } finally {
        setIsLoading(false);
      }
    };
    fetchDocuments();
  }, [selectedClientId]);

  const selectedClientLabel = useMemo(() => {
    const client = clients?.find(
      (c) => String(c.clientId ?? c.client_id) === String(selectedClientId),
    );
    const code = client?.clientId ?? client?.client_id;
    return client ? `${client.name || 'Unknown'} (${code || '-'})` : '';
  }, [clients, selectedClientId]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setFileItem(null);
      return;
    }
    setFileItem(file);
  };

  const handleUpload = (e) => {
    e.preventDefault();
    setMessage(null);
    if (!selectedClientId) {
      setMessage({ type: 'danger', text: 'Please select a buyer first.' });
      return;
    }
    if (!documentName.trim()) {
      setMessage({ type: 'danger', text: 'Document name is required.' });
      return;
    }
    if (!fileItem) {
      setMessage({ type: 'danger', text: 'Please select a file to upload.' });
      return;
    }

    const uploadAsync = async () => {
      try {
        setIsLoading(true);
        await documentService.uploadDocument(selectedClientId, documentName.trim(), fileItem);
        
        // Fetch updated documents list
        const docs = await documentService.getClientDocuments(selectedClientId);
        setDocuments(docs);
        if (docs.length > 0) {
          setLastUpdated(new Date(docs[0].uploadedAt).toISOString());
        }
        
        setDocumentName('');
        setFileItem(null);
        setMessage({ type: 'success', text: 'Document uploaded successfully.' });
      } catch (error) {
        console.error('Upload error:', error);
        setMessage({ 
          type: 'danger', 
          text: error.response?.data?.message || 'Failed to upload document.' 
        });
      } finally {
        setIsLoading(false);
      }
    };

    uploadAsync();
  };

  const handleDelete = (docId) => {
    if (!selectedClientId) return;
    
    const deleteAsync = async () => {
      try {
        setIsLoading(true);
        await documentService.deleteDocument(docId);
        
        // Fetch updated documents list
        const docs = await documentService.getClientDocuments(selectedClientId);
        setDocuments(docs);
        if (docs.length > 0) {
          setLastUpdated(new Date(docs[0].uploadedAt).toISOString());
        } else {
          setLastUpdated(null);
        }
        
        setMessage({ type: 'success', text: 'Document deleted successfully.' });
      } catch (error) {
        console.error('Delete error:', error);
        setMessage({ 
          type: 'danger', 
          text: error.response?.data?.message || 'Failed to delete document.' 
        });
      } finally {
        setIsLoading(false);
      }
    };

    deleteAsync();
  };

  const handleDownload = (doc) => {
    const downloadAsync = async () => {
      try {
        setIsLoading(true);
        await documentService.downloadDocument(doc.id, doc.fileName);
      } catch (error) {
        console.error('Download error:', error);
        setMessage({ 
          type: 'danger', 
          text: 'Failed to download document.' 
        });
      } finally {
        setIsLoading(false);
      }
    };

    downloadAsync();
  };

  return (
    <div className="page-shell">
      <PageHeader
        icon={<FaCloudUploadAlt size={22} />}
        title="DMS - Buyer Document Library"
        subtitle="Select a buyer to view documents and upload new files"
      />
      <div className="container-lg mt-2">

        {message && (
          <div className={`alert alert-${message.type} alert-dismissible fade show alert-rounded`} role="alert">
            {message.text}
            <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
          </div>
        )}

        <div className="card border-0 shadow-sm mb-4 dms-buyer-card">
          <div className="card-header bg-white border-0 pb-0">
            <h5 className="mb-0 dms-buyer-title">Buyer</h5>
          </div>
          <div className="card-body pt-3">
            <label htmlFor="dms-buyer-select" className="form-label fw-600 text-dark-700">
              Buyer name <span className="text-danger">*</span>
            </label>
            <select
              id="dms-buyer-select"
              className="form-select form-select-lg"
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(e.target.value)}
            >
              <option value="">Select buyer name</option>
              {clients.map((c) => {
                const apiClientId = c.clientId ?? c.client_id;
                const rowKey = c.id ?? apiClientId;
                return (
                  <option key={rowKey} value={apiClientId}>
                    {c.name || 'Unknown'} ({apiClientId || '-'})
                  </option>
                );
              })}
            </select>
          </div>
        </div>

        {selectedClientId && (
          <>
            <div className="card border-0 shadow-sm mb-4 dms-docs-card">
              <div className="card-header bg-white border-0 pb-0">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <h5 className="mb-0 dms-docs-title">
                    Documents for {selectedClientLabel || 'buyer'}
                  </h5>
                  <div className="text-muted small">
                    Last updated: {formatDateTime(lastUpdated)}
                  </div>
                </div>
              </div>
              <div className="card-body">
                {isLoading ? (
                  <div className="text-center text-muted py-4">
                    <div className="spinner-border spinner-border-sm me-2" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    Loading documents...
                  </div>
                ) : documents.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    No documents uploaded for this buyer yet.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover table-bordered align-middle">
                      <thead className="thead-muted">
                        <tr>
                          <th className="fw-600">Document Name</th>
                          <th className="fw-600">File Name</th>
                          <th className="fw-600">Uploaded At</th>
                          <th className="fw-600 w-140">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {documents.map((doc) => (
                          <tr key={doc.id}>
                            <td>{doc.documentName}</td>
                            <td>{doc.fileName}</td>
                            <td>{formatDateTime(doc.uploadedAt)}</td>
                            <td>
                              <div className="btn-group" role="group" aria-label="Document actions">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-success"
                                  onClick={() => handleDownload(doc)}
                                  disabled={isLoading}
                                  title="Download"
                                  aria-label={`Download ${doc.fileName || doc.documentName || 'document'}`}
                                >
                                  <FaDownload aria-hidden />
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleDelete(doc.id)}
                                  disabled={isLoading}
                                  title="Delete"
                                  aria-label={`Delete ${doc.documentName || doc.fileName || 'document'}`}
                                >
                                  <FaTrash aria-hidden />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <div className="card border-0 shadow-sm mb-4 dms-upload-card">
              <div className="card-header bg-white border-0 pb-0">
                <h5 className="mb-0 dms-upload-title">Add document for {selectedClientLabel || 'buyer'}</h5>
              </div>
              <div className="card-body">
                <form onSubmit={handleUpload}>
                  <div className="row">
                    <div className="col-lg-6 mb-3">
                      <label htmlFor="dms-document-name" className="form-label fw-600 text-dark-700">
                        Document name <span className="text-danger">*</span>
                      </label>
                      <input
                        id="dms-document-name"
                        className="form-control form-control-lg"
                        value={documentName}
                        onChange={(e) => setDocumentName(e.target.value)}
                        placeholder="Enter document name"
                      />
                    </div>
                    <div className="col-lg-6 mb-3">
                      <label htmlFor="dms-upload-file" className="form-label fw-600 text-dark-700">
                        Upload file (one at a time) <span className="text-danger">*</span>
                      </label>
                      <input
                        id="dms-upload-file"
                        type="file"
                        className="form-control"
                        onChange={handleFileChange}
                      />
                      {fileItem && (
                        <small className="text-muted">Selected: {fileItem.name}</small>
                      )}
                    </div>
                  </div>
                  <button type="submit" className="btn btn-success btn-rounded-bold" disabled={isLoading}>
                    <FaCloudUploadAlt className="me-2" aria-hidden /> {isLoading ? 'Uploading...' : 'Upload document'}
                  </button>
                </form>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DMS;
